/**
 * Parser for Apple's **Password Rules** grammar (remote-site-rules P3).
 *
 * A site's `password-rules` string (e.g.
 * `minlength: 8; maxlength: 32; required: lower, upper, digit; allowed: [!#$];`)
 * describes what a *compatible* password for that site looks like. We parse it to
 * pre-configure the generator so a generated password satisfies the site on the
 * first try. Grammar: https://developer.apple.com/password-rules/ — ported from
 * the reference `tools/PasswordRulesParser.js` in
 * `third_party/password-manager-resources/` (MIT).
 *
 * This module is **pure data**: it parses a string into constraints and maps
 * those onto generator options. It executes **no** site-supplied input — the
 * `password-rules` string is data, never code (MV3-safe).
 *
 * The parser is **total**: it never throws. Unknown tokens are ignored
 * (forward-compatible); an input with nothing recognizable returns `null` so the
 * caller falls back to the user's default generator settings.
 */

/** The named character classes in the grammar. `ascii-printable`/`unicode` mean "any". */
export type NamedCharClass =
  | 'upper'
  | 'lower'
  | 'digit'
  | 'special'
  | 'ascii-printable'
  | 'unicode';

/** A grammar character class: a named class, or a literal `[…]` custom set. */
export type CharClass = NamedCharClass | { readonly custom: string };

/** Normalized constraints extracted from a `password-rules` string. */
export interface PasswordRuleConstraints {
  minLength?: number;
  maxLength?: number;
  maxConsecutive?: number;
  /** Classes that must EACH appear at least once. */
  required: CharClass[];
  /** Classes permitted (already includes `required`; `ascii-printable`/`unicode` ⇒ any). */
  allowed: CharClass[];
}

/** The generator option shape shared by the web + extension clients (structural). */
export interface GeneratorCharsetOptions {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  numbers: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
  /**
   * When set, restricts the generator's symbol pool to exactly these characters
   * instead of the client's default symbol set. Populated by
   * {@link constraintsToGeneratorOptions} when a site rule permits symbols only
   * from a specific `[…]` set (e.g. Citi allows ``~`!@#$%^&*()_-/\|`` but forbids
   * `,` `]` `{` — which the default pool contains), so a generated password can
   * never include a symbol the site rejects. Absent ⇒ the default pool is used.
   */
  symbolSet?: string;
}

const NAMED_CLASSES: ReadonlySet<string> = new Set<NamedCharClass>([
  'upper', 'lower', 'digit', 'special', 'ascii-printable', 'unicode',
]);
const RULE_NAMES: ReadonlySet<string> = new Set([
  'minlength', 'maxlength', 'max-consecutive', 'required', 'allowed',
]);

/**
 * Split a `required`/`allowed` value into tokens, respecting `[…]` custom classes
 * (which may contain commas, spaces, `[`, and a doubled `]]` meaning a literal
 * `]`). Named tokens are comma-separated; whitespace is trimmed. Always advances,
 * so it terminates on any input.
 */
function tokenizeValue(raw: string): CharClass[] {
  const out: CharClass[] = [];
  let i = 0;
  const n = raw.length;
  while (i < n) {
    // Skip whitespace and stray separators between tokens.
    const ch = raw[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === ',') {
      i++;
      continue;
    }
    if (ch === '[') {
      // Custom character class: capture chars until the closing ']'. A ']'
      // immediately followed by another ']' is a literal ']' (Apple's convention);
      // otherwise ']' closes the class.
      i++; // past '['
      let chars = '';
      while (i < n) {
        const c = raw[i];
        if (c === ']') {
          if (raw[i + 1] === ']') { chars += ']'; i += 2; continue; }
          i++; // consume the closing ']'
          break;
        }
        chars += c;
        i++;
      }
      if (chars.length) out.push({ custom: chars });
      continue;
    }
    // Named identifier: [a-zA-Z-]+
    let j = i;
    while (j < n && /[a-zA-Z-]/.test(raw[j])) j++;
    const ident = raw.slice(i, j).toLowerCase();
    if (NAMED_CLASSES.has(ident)) out.push(ident as NamedCharClass);
    // Unknown identifier → ignored (forward-compatible). Advance past it.
    i = j > i ? j : i + 1;
  }
  return out;
}

function parseIntOrNull(raw: string): number | null {
  const m = raw.match(/^\s*(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Parse a `password-rules` string into normalized constraints, or `null` if
 * nothing usable is present. Never throws. Aggregation matches the reference
 * parser: the strictest of repeated bounds wins (max of `minlength`, min of
 * `maxlength` / `max-consecutive`), and `required` classes are also `allowed`.
 */
export function parsePasswordRules(text: string): PasswordRuleConstraints | null {
  if (typeof text !== 'string' || !text.trim()) return null;
  try {
    let minLength: number | undefined;
    let maxLength: number | undefined;
    let maxConsecutive: number | undefined;
    const required: CharClass[] = [];
    const allowedOnly: CharClass[] = [];
    let sawAllowedProperty = false;
    let sawAny = false;

    for (const segment of text.split(';')) {
      const idx = segment.indexOf(':');
      if (idx < 0) continue;
      const name = segment.slice(0, idx).trim().toLowerCase();
      const value = segment.slice(idx + 1).trim();
      if (!RULE_NAMES.has(name)) continue; // unknown property → ignore

      switch (name) {
        case 'minlength': {
          const v = parseIntOrNull(value);
          if (v !== null) { minLength = minLength === undefined ? v : Math.max(minLength, v); sawAny = true; }
          break;
        }
        case 'maxlength': {
          const v = parseIntOrNull(value);
          if (v !== null) { maxLength = maxLength === undefined ? v : Math.min(maxLength, v); sawAny = true; }
          break;
        }
        case 'max-consecutive': {
          const v = parseIntOrNull(value);
          if (v !== null) { maxConsecutive = maxConsecutive === undefined ? v : Math.min(maxConsecutive, v); sawAny = true; }
          break;
        }
        case 'required': {
          const classes = tokenizeValue(value);
          if (classes.length) { required.push(...classes); sawAny = true; }
          break;
        }
        case 'allowed': {
          sawAllowedProperty = true;
          const classes = tokenizeValue(value);
          if (classes.length) { allowedOnly.push(...classes); sawAny = true; }
          break;
        }
      }
    }

    if (!sawAny) return null;

    // `required` classes are implicitly `allowed` too (reference parser copies
    // them). If no `allowed` was given at all, the default permitted set is any
    // printable ASCII.
    let allowed = dedupeClasses([...allowedOnly, ...required]);
    if (!allowed.length && !sawAllowedProperty) allowed = ['ascii-printable'];

    return {
      ...(minLength !== undefined ? { minLength } : {}),
      ...(maxLength !== undefined ? { maxLength } : {}),
      ...(maxConsecutive !== undefined ? { maxConsecutive } : {}),
      required: dedupeClasses(required),
      allowed,
    };
  } catch {
    return null; // totality guarantee — a parse bug degrades to "no rule"
  }
}

function dedupeClasses(classes: CharClass[]): CharClass[] {
  const named = new Set<string>();
  const out: CharClass[] = [];
  for (const c of classes) {
    if (typeof c === 'string') {
      if (!named.has(c)) { named.add(c); out.push(c); }
    } else {
      out.push(c); // customs kept as-is; categorization folds them later
    }
  }
  return out;
}

/**
 * The exact set of non-alphanumeric characters a rule permits, or `undefined`
 * when the symbol pool is *not* restricted to a specific set — either because an
 * "any character" symbol class is allowed (`special` / `ascii-printable` /
 * `unicode`), or because no symbols are allowed at all.
 *
 * When defined (a non-empty string), the generator must draw its symbol
 * characters ONLY from it, so a generated password can never contain a symbol the
 * site rejects. Only non-alphanumerics constrain the pool; letters and digits are
 * governed by the upper/lower/digit toggles, which the generator can't narrow to
 * an arbitrary subset (unchanged limitation).
 */
export function allowedSymbolSet(c: PasswordRuleConstraints): string | undefined {
  let set = '';
  for (const cls of c.allowed) {
    if (cls === 'special' || cls === 'ascii-printable' || cls === 'unicode') {
      return undefined; // any symbol permitted → no restriction to apply
    }
    if (typeof cls === 'object') {
      for (const ch of cls.custom) {
        if (!/[A-Za-z0-9]/.test(ch) && !set.includes(ch)) set += ch;
      }
    }
  }
  return set.length ? set : undefined;
}

/** Which base char-set toggles a literal custom set implies. */
function categorizeCustom(chars: string, sets: { up: boolean; lo: boolean; di: boolean; sy: boolean }): void {
  for (const ch of chars) {
    if (ch >= 'A' && ch <= 'Z') sets.up = true;
    else if (ch >= 'a' && ch <= 'z') sets.lo = true;
    else if (ch >= '0' && ch <= '9') sets.di = true;
    else sets.sy = true; // any other printable (space, punctuation) → symbol
  }
}

/**
 * Map parsed constraints onto a client's generator options, returning a copy of
 * `base` with length + char-set toggles adjusted to satisfy the rule. Pure; the
 * actual byte generation stays in each client's `generatePassword`.
 *
 * - Char sets come from `allowed` (which already includes `required`), so every
 *   `required` class is enabled and disallowed classes are off. `ascii-printable`
 *   / `unicode` enable all four. A custom `[…]` set enables whichever base
 *   toggles its characters fall into.
 * - `symbolSet` is set from {@link allowedSymbolSet} when the rule restricts
 *   symbols to a specific `[…]` set, so the generator's symbol pool is exactly the
 *   site's permitted specials (else `undefined` ⇒ the client's default pool).
 * - Length is `base.length` clamped into `[minLength, maxLength]`.
 * - `max-consecutive` can't be expressed by the current generators, so it's
 *   ignored (documented limitation — worst case the user regenerates).
 * - If the rule would disable every char set (degenerate), `base`'s toggles are
 *   kept so the generator never runs with an empty alphabet.
 */
export function constraintsToGeneratorOptions<T extends GeneratorCharsetOptions>(
  c: PasswordRuleConstraints,
  base: T,
): T {
  const sets = { up: false, lo: false, di: false, sy: false };
  let any = false;
  for (const cls of c.allowed) {
    if (cls === 'ascii-printable' || cls === 'unicode') {
      sets.up = sets.lo = sets.di = sets.sy = true;
    } else if (cls === 'upper') sets.up = true;
    else if (cls === 'lower') sets.lo = true;
    else if (cls === 'digit') sets.di = true;
    else if (cls === 'special') sets.sy = true;
    else categorizeCustom(cls.custom, sets);
  }
  any = sets.up || sets.lo || sets.di || sets.sy;

  let length = base.length;
  if (c.minLength !== undefined) length = Math.max(length, c.minLength);
  if (c.maxLength !== undefined) length = Math.min(length, c.maxLength);
  if (length < 1) length = 1;

  return {
    ...base,
    length,
    uppercase: any ? sets.up : base.uppercase,
    lowercase: any ? sets.lo : base.lowercase,
    numbers: any ? sets.di : base.numbers,
    symbols: any ? sets.sy : base.symbols,
    // Restrict the symbol pool to the site's allowed specials (or clear a stale
    // restriction when the rule permits any symbol). `undefined` ⇒ default pool.
    symbolSet: allowedSymbolSet(c),
  };
}

/** Does the password contain at least one character of the given class? */
function classPresent(password: string, cls: CharClass): boolean {
  if (typeof cls === 'object') {
    return [...password].some((ch) => cls.custom.includes(ch));
  }
  switch (cls) {
    case 'upper': return /[A-Z]/.test(password);
    case 'lower': return /[a-z]/.test(password);
    case 'digit': return /[0-9]/.test(password);
    case 'special': return /[^A-Za-z0-9]/.test(password);
    // "any character" classes are satisfied by a non-empty password.
    case 'ascii-printable':
    case 'unicode': return password.length > 0;
  }
}

/** True if the password has a run of the same character longer than `max`. */
function violatesMaxConsecutive(password: string, max: number): boolean {
  let run = 1;
  for (let i = 1; i < password.length; i++) {
    if (password[i] === password[i - 1]) {
      run++;
      if (run > max) return true;
    } else {
      run = 1;
    }
  }
  return false;
}

/**
 * Does a password satisfy the parts of a rule that generation can influence: the
 * length bounds, each `required` class present (named classes, or ≥1 char of a
 * required `[…]` set), `max-consecutive`, and — for the symbol pool only —
 * *allowed*-only (no symbol outside the site's permitted set). Pure, never throws.
 *
 * The allowed-only check is scoped to non-alphanumerics because that's the half
 * the generator can actually honor (via {@link GeneratorCharsetOptions.symbolSet},
 * populated from {@link allowedSymbolSet}). Letters and digits are still not
 * restricted to arbitrary subsets — the generators map a custom letter/digit set
 * to whole char-set toggles — so those remain an accepted limitation (documented
 * in the P3 plan). This keeps the retry in {@link generatePasswordForRules} from
 * failing on passwords it correctly produced.
 */
export function satisfiesPasswordRules(password: string, c: PasswordRuleConstraints): boolean {
  if (c.minLength !== undefined && password.length < c.minLength) return false;
  if (c.maxLength !== undefined && password.length > c.maxLength) return false;
  if (c.maxConsecutive !== undefined && violatesMaxConsecutive(password, c.maxConsecutive)) return false;
  for (const cls of c.required) {
    if (!classPresent(password, cls)) return false;
  }
  const symbols = allowedSymbolSet(c);
  if (symbols !== undefined) {
    for (const ch of password) {
      if (!/[A-Za-z0-9]/.test(ch) && !symbols.includes(ch)) return false;
    }
  }
  return true;
}

/**
 * The constraints implied by the generator's OWN checkboxes — every class the
 * user enabled must actually appear.
 *
 * This exists because the generator draws classes by weight, not by guarantee:
 * symbols carry only 10% weight, so a 16-character password omits them
 * entirely about 18% of the time (0.9^16). Ticking "symbols" and getting a
 * password without one is the visible result, and it is what
 * {@link generatePasswordForRules} already fixes — but until now that retry was
 * only wired to SITE rules, so the standalone generator never benefited.
 *
 * Feed the result to {@link generatePasswordForRules} alongside the client's
 * own `generatePassword`. When no class is enabled the required list is empty:
 * the generator falls back to lowercase+digits internally, and demanding
 * classes the user switched off would spin the retry for nothing.
 */
export function constraintsFromGeneratorOptions(
  options: GeneratorCharsetOptions,
): PasswordRuleConstraints {
  const required: CharClass[] = [];
  if (options.uppercase) required.push('upper');
  if (options.lowercase) required.push('lower');
  if (options.numbers) required.push('digit');
  if (options.symbols) required.push('special');
  // `allowed` mirrors `required` — the caller's generator already builds its
  // pools from these same flags, so nothing outside them can appear.
  return { required, allowed: [...required] };
}

/** Default retry budget for {@link generatePasswordForRules}. */
export const PASSWORD_RULE_MAX_ATTEMPTS = 30;

/**
 * Generate a password that satisfies `constraints`, by calling `generate` (a
 * client's `generatePassword` bound to rule-derived options) and **retrying until
 * the candidate passes `satisfiesPasswordRules`** — because the generator samples
 * character classes by weight and doesn't otherwise guarantee that every
 * `required` class appears.
 *
 * **Bounded**: after `maxAttempts` it returns the last candidate (best-effort),
 * so an impossible or user-contradicted spec can never hang the UI. Each attempt
 * is cheap, so a handful of retries is invisible; the common case succeeds on the
 * first or second try.
 */
export function generatePasswordForRules(
  constraints: PasswordRuleConstraints,
  generate: () => string,
  maxAttempts: number = PASSWORD_RULE_MAX_ATTEMPTS,
): string {
  let password = generate();
  for (let i = 1; i < maxAttempts && !satisfiesPasswordRules(password, constraints); i++) {
    password = generate();
  }
  return password;
}
